import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, VariantSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initPdpThumbnailHeightSync();
  }

  #controller = new AbortController();

  /** @type {ResizeObserver | null} */
  #pdpThumbnailHeightObserver = null;

  /** @type {MediaQueryList | null} */
  #pdpThumbnailDesktopQuery = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#pdpThumbnailHeightObserver?.disconnect();
    this.#pdpThumbnailHeightObserver = null;
    this.#pdpThumbnailDesktopQuery?.removeEventListener('change', this.#syncPdpThumbnailHeight);
    this.#pdpThumbnailDesktopQuery = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const { html, productId } = event.detail.data;
    const variant = event.detail.resource;

    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const source = html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const featuredMediaId = variant?.featured_media?.id?.toString();

    this.replaceWith(newMediaGallery);

    if (newMediaGallery instanceof MediaGallery) {
      queueMicrotask(() => {
        newMediaGallery.syncToSelectedVariant(featuredMediaId);
      });
    }
  };

  /**
   * Reorders slides immediately when a matrix variant option is selected.
   *
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const variantPicker = event.target instanceof HTMLElement ? event.target.closest('variant-picker') : null;

    if (!variantPicker?.querySelector('.variant-option--matrix-row')) return;
    if (variantPicker.dataset.productId && this.dataset.productId && variantPicker.dataset.productId !== this.dataset.productId) {
      return;
    }

    this.#syncGalleryToMatrixSelection();
  };

  /**
   * Reorders and selects gallery media for the active matrix row selection.
   * @param {string | undefined} [featuredMediaId] - Featured media id from the selected variant.
   */
  #syncGalleryToMatrixSelection(featuredMediaId) {
    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector(
      'variant-picker .variant-option--matrix-row'
    )?.closest('variant-picker');

    if (!variantPicker) return;

    const checkedInput = variantPicker.querySelector('.variant-option--matrix-row:not([hidden]) input:checked');

    if (!(checkedInput instanceof HTMLInputElement)) return;

    const priorityIds = this.#getMatrixRowMediaIds(checkedInput);
    const selectedMediaId = featuredMediaId || checkedInput.dataset.optionMediaId;

    if (selectedMediaId) {
      const normalizedSelectedId = String(selectedMediaId);
      const selectedIndex = priorityIds.indexOf(normalizedSelectedId);

      if (selectedIndex > 0) {
        priorityIds.splice(selectedIndex, 1);
        priorityIds.unshift(normalizedSelectedId);
      } else if (selectedIndex === -1) {
        priorityIds.unshift(normalizedSelectedId);
      }
    }

    if (priorityIds.length) {
      this.#reorderSlidesByMediaIds(priorityIds);
    }

    this.#syncGalleryToVariant(selectedMediaId);
  }

  /**
   * Selects the featured media slide after the gallery is replaced.
   * @param {string | undefined} featuredMediaId
   */
  syncToSelectedVariant(featuredMediaId) {
    this.#syncGalleryToVariant(featuredMediaId);
  }

  /**
   * @param {string | undefined} featuredMediaId
   */
  #syncGalleryToVariant(featuredMediaId) {
    if (!featuredMediaId) {
      this.slideshow?.select(0, undefined, { animate: false });
      this.#syncPdpThumbnailHeight();
      return;
    }

    this.slideshow?.select({ id: String(featuredMediaId) }, undefined, { animate: false });
    this.#syncPdpThumbnailHeight();
  }

  /**
   * Collects featured media ids for variants in the active matrix row.
   * @param {HTMLInputElement} checkedInput
   * @returns {string[]}
   */
  #getMatrixRowMediaIds(checkedInput) {
    const fieldset = checkedInput.closest('.variant-option--matrix-row');
    if (!(fieldset instanceof HTMLElement)) return [];

    /** @type {string[]} */
    const mediaIds = [];

    fieldset.querySelectorAll('input[data-option-media-id]').forEach((input) => {
      if (input instanceof HTMLInputElement && input.dataset.optionMediaId) {
        mediaIds.push(String(input.dataset.optionMediaId));
      }
    });

    return [...new Set(mediaIds)];
  }

  /**
   * Moves slideshow slides so related media appear first.
   * @param {string[]} priorityIds
   */
  #reorderSlidesByMediaIds(priorityIds) {
    const slideshow = this.refs.slideshow;
    const slides = slideshow?.refs.slides;

    if (!slides?.length) return;

    const slidesContainer = slides[0]?.parentElement;
    if (!slidesContainer) return;

    const normalizedPriorityIds = priorityIds.map(String);
    const prioritySet = new Set(normalizedPriorityIds);

    /** @type {Map<string, HTMLElement>} */
    const slidesById = new Map();

    for (const slide of slides) {
      const slideId = slide.getAttribute('slide-id');
      if (slideId) slidesById.set(String(slideId), slide);
    }

    /** @type {HTMLElement[]} */
    const orderedSlides = [];

    for (const id of normalizedPriorityIds) {
      const slide = slidesById.get(id);
      if (slide) orderedSlides.push(slide);
    }

    for (const slide of slides) {
      const slideId = slide.getAttribute('slide-id');
      if (!slideId || !prioritySet.has(String(slideId))) {
        orderedSlides.push(slide);
      }
    }

    for (const slide of orderedSlides) {
      slidesContainer.appendChild(slide);
    }

    this.#reorderThumbnails(normalizedPriorityIds);
    this.#syncPdpThumbnailHeight();
  }

  /**
   * Keeps thumbnail controls aligned with reordered slides.
   * @param {string[]} priorityIds
   */
  #reorderThumbnails(priorityIds) {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    const thumbnailLists = slideshow.querySelectorAll('.slideshow-controls__thumbnails');

    thumbnailLists.forEach((thumbnailList) => {
      if (!(thumbnailList instanceof HTMLElement)) return;

      /** @type {Map<string, HTMLElement>} */
      const controlsByMediaId = new Map();

      thumbnailList.querySelectorAll('[data-thumbnail-media-id]').forEach((control) => {
        if (!(control instanceof HTMLElement)) return;
        const mediaId = control.dataset.thumbnailMediaId;
        if (mediaId) controlsByMediaId.set(String(mediaId), control);
      });

      /** @type {HTMLElement[]} */
      const orderedControls = [];
      const prioritySet = new Set(priorityIds.map(String));

      for (const id of priorityIds) {
        const control = controlsByMediaId.get(String(id));
        if (control) orderedControls.push(control);
      }

      thumbnailList.querySelectorAll('[data-thumbnail-media-id]').forEach((control) => {
        if (!(control instanceof HTMLElement)) return;
        const mediaId = control.dataset.thumbnailMediaId;
        if (!mediaId || !prioritySet.has(String(mediaId))) {
          orderedControls.push(control);
        }
      });

      orderedControls.forEach((control, index) => {
        control.setAttribute('on:click', `/select/${index}`);
        thumbnailList.appendChild(control);
      });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {import('./events').ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Keeps desktop PDP thumbnail controls within the main image height.
   */
  #initPdpThumbnailHeightSync() {
    if (!this.classList.contains('product-media-gallery--pdp')) return;

    queueMicrotask(() => {
      this.#setupPdpThumbnailHeightSync();
      this.#syncPdpThumbnailHeight();
    });
  }

  #setupPdpThumbnailHeightSync() {
    if (!this.classList.contains('product-media-gallery--pdp')) return;

    this.#pdpThumbnailHeightObserver?.disconnect();

    this.#pdpThumbnailDesktopQuery?.removeEventListener('change', this.#syncPdpThumbnailHeight);
    this.#pdpThumbnailDesktopQuery = window.matchMedia('(min-width: 990px)');
    this.#pdpThumbnailDesktopQuery.addEventListener('change', this.#syncPdpThumbnailHeight);

    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#syncPdpThumbnailHeight, {
      signal: this.#controller.signal,
    });

    this.#pdpThumbnailHeightObserver = new ResizeObserver(() => {
      this.#syncPdpThumbnailHeight();
    });

    const container = slideshow.querySelector('slideshow-container');
    if (container) this.#pdpThumbnailHeightObserver.observe(container);

    slideshow
      .querySelectorAll('.product-media, .product-media-container img, .product-media-container video')
      .forEach((media) => {
        this.#pdpThumbnailHeightObserver.observe(media);
      });
  }

  #getPdpMainMediaHeight(slideshow) {
    const container = slideshow.querySelector('slideshow-container');
    const visibleSlide =
      container?.querySelector('slideshow-slide[aria-hidden="false"]') ??
      container?.querySelector('slideshow-slide');
    const mediaContainer = visibleSlide?.querySelector('.product-media-container');
    const media = mediaContainer?.querySelector('.product-media img, .product-media video, .product-media');

    return (
      media?.getBoundingClientRect().height ??
      mediaContainer?.getBoundingClientRect().height ??
      container?.getBoundingClientRect().height ??
      0
    );
  }

  #syncPdpThumbnailHeight = () => {
    if (!this.classList.contains('product-media-gallery--pdp')) return;

    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    if (!window.matchMedia('(min-width: 990px)').matches) {
      slideshow.style.removeProperty('--pdp-thumbnails-max-height');
      return;
    }

    const controls = slideshow.querySelector('slideshow-controls:not(.media-gallery__mobile-controls)[thumbnails]');
    if (!controls) {
      slideshow.style.removeProperty('--pdp-thumbnails-max-height');
      return;
    }

    const height = this.#getPdpMainMediaHeight(slideshow);

    if (height > 0) {
      slideshow.style.setProperty('--pdp-thumbnails-max-height', `${Math.round(height)}px`);
    }
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
